import { AppIcon, AppText } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppPlaceholderView from "@toothfairy/shared-ui/AppPlaceholderView";
import AppTextInput from "@toothfairy/shared-ui/AppTextInput";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Tree } from "react-arborist";
import { useWindowDimensions, View } from "react-native";
import { Popable } from "react-native-popable";
import { TreeviewProvider } from "../../Hooks/useTreeView";
import { minAppWidth } from "../../Screens/MainViewContainer";
import Utils from "./Components/Utils";
function AppTreeView({
  foldersData,
  title,
  handleClickOnNode,
  selectedNode,
  setDialogActionOnTree,
  deleteNode,
  isEmpty,
  onDrop,
  onDropFilesOnNode = () => {},
  initialOpen,
  showCollapseTree = false,
  isCollapsed,
  setIsCollapsed,
  showIconsOnSelection = false,
  requireNormalisation = true,
  onMove,
  deleteAction,
  editLeafAction,
  editLeafParentAction,
  allowParentEdit = true,
  fixedNodeIds,
  heightOffset = 200,
  indent = 22,
  minWidth = 330,
  disableTFDrop,
  disableTFDrag,
  preventSortingByName,
  filterPlaceholder = "Filter",
  treemode = "DEFAULT",
  secondaryActionTitle,
  secondaryActionCallback = () => {},
  setSelectedNodeForTreeView,
  allowParentAction,
  onParentActionClick = () => {},
  headerComponents = [],
  secondaryActionTitlePopup = null,
  onToggleFavorite = () => {},
  isHomeMode = false,
  isMultiDeleteMode = false,
  multiSelectedIds = [],
  onToggleNodeSelection = (v) => {},
  ...props
}) {
  const { t } = useTranslation();
  const treeRef = useRef();
  const { height } = useWindowDimensions();
  const [treeData, setTreeData] = useState(null);
  const [term, setTerm] = useState("");
  function listToTree(data, options) {
    options = options || {};
    var ID_KEY = options.idKey || "id";
    var PARENT_KEY = options.parentKey || "parent";
    var CHILDREN_KEY = options.childrenKey || "children";

    var tree = [],
      childrenOf = {};
    var item, id, parentId;

    // Extract all IDs first to check parent references
    var allIds = data.map((d) => d[ID_KEY]);

    for (var i = 0, length = data.length; i < length; i++) {
      item = data[i];
      id = item[ID_KEY];
      parentId = item[PARENT_KEY] || "root";

      var parentExists = allIds.includes(parentId);

      // every item may have children
      childrenOf[id] = childrenOf[id] || [];

      // init its children
      item[CHILDREN_KEY] = childrenOf[id];

      if (parentId != "root" && parentExists) {
        // init its parent's children object
        childrenOf[parentId] = childrenOf[parentId] || [];
        // push it into its parent's children object
        childrenOf[parentId].push(item);
      } else if (parentId != "root" && !parentExists) {
        // Parent doesn't exist in data - this item becomes a root
        tree.push(item);
      } else {
        tree.push(item);
      }
    }

    return tree;
  }
  const normaliseDataForTree = () => {
    // using the parentid inside the flat array folderData recursively group the data inside a children array
    const result = listToTree(foldersData, {
      idKey: "id",
      parentKey: "parent",
    });
    return result;
  };
  function sortTree(tree) {
    if (preventSortingByName) return tree;

    // Sort the array by separating folders and files first, then by name
    tree.sort((a, b) => {
      // Assuming there's a property 'isFolder' or 'type' to distinguish folders from files
      const isAFolder = a.droppable;
      const isBFolder = b.droppable;

      // If both are folders or both are files, sort by name
      if (isAFolder === isBFolder) {
        return a?.name?.localeCompare(b?.name);
      }

      // If one is a folder and one is a file, put folder first
      return isAFolder ? -1 : 1;
    });

    // Sort children arrays recursively
    tree.forEach((item) => {
      if (item?.children && item.children.length > 0) {
        sortTree(item.children);
      }
    });

    return tree;
  }

  useEffect(() => {
    if (requireNormalisation) setTreeData(sortTree(normaliseDataForTree()));
    else setTreeData(sortTree(foldersData));
  }, [foldersData]);
  const ref = React.useRef(null);
  const [availableWidth, setAvailableWidth] = React.useState(0);
  const handleDrop = (newTreeData, rest) => {
    const { dragSourceId, dropTargetId } = rest;
    onDrop({
      newTreeData,
      dragSourceId,
      dropTargetId,
      rest,
    });
  };

  useEffect(() => {
    setAvailableWidth(ref.current.clientWidth - 50);
  });

  // Auto-expand tree to show selected node when component mounts or selection changes
  useEffect(() => {
    if (treeRef.current && selectedNode?.id && treeData) {
      // Small delay to ensure tree is fully rendered
      setTimeout(() => {
        const tree = treeRef.current;
        if (tree) {
          // Open all parent nodes to reveal the selected node
          tree.openParents(selectedNode.id);
          // Ensure the node is selected
          tree.select(selectedNode.id);
          // Focus on the selected node to ensure it's visible
          tree.focus(selectedNode.id);
        }
      }, 100);
    }
  }, [selectedNode?.id]);

  const { currentTheme, mode } = AppTheme.useTheme();
  return (
    <TreeviewProvider
      onSelectionChanged={handleClickOnNode}
      onDrop={handleDrop}
      onDeletion={deleteNode}
      setDialogActionOnTree={setDialogActionOnTree}
      showIconsOnSelection={showIconsOnSelection}
      selectedNode={selectedNode}
      treeData={treeData}
      deleteAction={deleteAction}
      editLeafAction={editLeafAction}
      editLeafParentAction={editLeafParentAction}
      allowParentEdit={allowParentEdit}
      allowParentAction={allowParentAction}
      onParentActionClick={onParentActionClick}
      fixedNodeIds={fixedNodeIds}
      treemode={treemode}
      onToggleFavorite={onToggleFavorite}
      isMultiDeleteMode={isMultiDeleteMode}
      multiSelectedIds={multiSelectedIds}
      onToggleNodeSelection={onToggleNodeSelection}
      onDropFilesOnNode={onDropFilesOnNode}
    >
      <div
        ref={ref}
        style={{
          marginTop: isCollapsed ? 7 : 5,
          zIndex: 10,
          maxWidth: isCollapsed ? 18 : "35%",
          minWidth: !isCollapsed && (minWidth || minAppWidth() - 50),
          backgroundColor: currentTheme?.theme?.backgroundColor,
          transition: "max-width 0.3s ease-in-out, min-width 0.3s ease-in-out",
          overflow: "hidden",
        }}
        id="main-tree-view"
        onClick={(e) => {
          // get element clicked on
          if (e.target.id === "main-tree-view") {
            handleClickOnNode(null);
          }
        }}
      >
        <View
          style={{
            flexDirection: "column",
          }}
        >
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              zIndex: 10,
            }}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              {showCollapseTree && (
                <Popable
                  numberOfLines={4}
                  backgroundColor={currentTheme?.theme?.tooltipBackgroundColor}
                  style={[
                    {
                      top: 30,
                      left: 20,
                      zIndex: 20,
                      maxWidth: 400,
                    },
                  ]}
                  content={
                    isCollapsed ? t("ui.showSidebar") : t("ui.collapseSidebar")
                  }
                  action="hover"
                  position={isCollapsed ? "bottom" : "bottom"}
                >
                  <AppIcon
                    color={
                      isCollapsed
                        ? currentTheme?.theme?.primary
                        : currentTheme?.theme?.font_color
                    }
                    icon={AppAssets.icons.sidebar}
                    isClickable
                    onPress={() => {
                      setIsCollapsed(!isCollapsed);
                    }}
                    wrapperStyle={{
                      position: "relative",
                    }}
                  />
                </Popable>
              )}
              {!isCollapsed &&
                headerComponents
                  .slice(0, 8)
                  ?.filter((u) => u)
                  .map((component, index) => (
                    <View
                      key={index}
                      style={{ marginLeft: index === 0 ? 5 : 0 }}
                    >
                      {component}
                    </View>
                  ))}
            </View>
            <View
              style={{
                flex: 1,
                flexDirection: "row",
                justifyContent: "flex-end",
                alignItems: "center",
              }}
            >
              {title && !isCollapsed && (
                <AppText
                  wrapperStyle={{
                    marginLeft: 5,
                  }}
                  fontWeight="bold"
                >
                  {title}
                </AppText>
              )}
              {!isCollapsed && (
                <Popable
                  numberOfLines={3}
                  backgroundColor={currentTheme?.theme?.tooltipBackgroundColor}
                  style={[
                    {
                      top: 30,
                      zIndex: 20,
                      maxWidth: 300,
                    },
                  ]}
                  content={t("ui.collapseSidebar")}
                  action="hover"
                  position={"left"}
                >
                  <AppIcon
                    color={currentTheme?.theme?.darkGrey}
                    icon={AppAssets.icons.sidebar}
                    isClickable
                    onPress={() => {
                      setIsCollapsed(true);
                    }}
                    wrapperStyle={{
                      marginLeft: 5,
                    }}
                  />
                </Popable>
              )}
              {isCollapsed && (
                <Popable
                  numberOfLines={3}
                  backgroundColor={currentTheme?.theme?.tooltipBackgroundColor}
                  style={[
                    {
                      top: 30,
                      zIndex: 20,
                      maxWidth: 300,
                    },
                  ]}
                  content={t("ui.expandSidebar")}
                  action="hover"
                  position={"bottom"}
                >
                  <AppIcon
                    color={currentTheme?.theme?.orange}
                    icon={AppAssets.icons.sidebar}
                    isClickable
                    onPress={() => {
                      setIsCollapsed(false);
                    }}
                    wrapperStyle={{
                      marginRight: 0,
                      marginLeft: 20,
                    }}
                  />
                </Popable>
              )}
            </View>
          </View>
          {!isCollapsed && (
            <AppTextInput
              editable={true}
              iconColor={currentTheme?.theme?.grey}
              value={term}
              onChangeText={(text) => setTerm(text)}
              placeholder={filterPlaceholder}
              leftIcon={AppAssets.icons.search}
              textContainer={{
                paddingHorizontal: 10,
                marginLeft: 0,
              }}
              style={{
                marginLeft: 5,
              }}
            />
          )}
          {!isCollapsed && isEmpty && treemode === "CHATS" && (
            <AppPlaceholderView
              accessibilityLabel="empty-chats-placeholder"
              wrapperStyle={{
                height: 150,
                marginVertical: 20,
                width: "auto",
              }}
              size={24}
              icon={AppAssets.icons.orbit}
              mainCaption={t("chats.selectChatCaption")}
              subCaption={t("chats.selectChatSubCaption")}
            />
          )}
          {!isCollapsed && treeData && (
            <Tree
              ref={treeRef}
              selection={selectedNode?.id}
              className="tree"
              openByDefault={initialOpen}
              data={treeData}
              width={"100%"}
              height={height - heightOffset}
              rowHeight={32}
              indent={indent}
              // renderCursor={Utils.Cursor}
              searchTerm={term}
              onMove={onMove}
              // renderRow={Utils.Row}
              paddingBottom={32}
              renderDragPreview={Utils.DragPreview}
              disableEdit={(data) => data.readOnly}
              // renderDragPreview={Utils.DragPreview}
              disableDrop={({ parentNode }) => {
                return disableTFDrop || parentNode.data.droppable !== true;
              }}
              disableDrag={(data) =>
                disableTFDrag ||
                data.id === "mrcRoot" ||
                data.id === "qaRoot" ||
                data.id === "domainRoot" ||
                data.id === "artifactsRoot"
              }
              onClick={() => {
                setSelectedNodeForTreeView && setSelectedNodeForTreeView(null);
              }}
              dndRootElement={ref.current}
            >
              {Utils.Node}
            </Tree>
          )}
        </View>
      </div>
    </TreeviewProvider>
  );
}

export default AppTreeView;
